import { FlagState, Stat, dasherizeParts } from "@timing71/common";
import { parseTime } from "../utils.js";
import { findTimetabledSession } from "./utils.js";

const colSpec = [
  Stat.NUM,
  Stat.STATE,
  Stat.DRIVER,
  Stat.TEAM,
  Stat.LAPS,
  Stat.TYRE,
  Stat.TYRE_STINT,
  Stat.TYRE_AGE,
  Stat.GAP,
  Stat.INT,
  Stat.S1,
  Stat.S2,
  Stat.S3,
  Stat.SPEED,
  Stat.LAST_LAP,
  Stat.BEST_LAP,
  Stat.PITS
];

const trackDataSpec = [
  "Track Temp",
  "Air Temp",
  "Wind Speed",
  "Direction",
  "Humidity",
  "Pressure",
  "Track"
];

function toTitleCase(str) {
  return str.replace(
    /\w\S*/g,
    function(txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    }
  );
}

export const getManifest = (state) => {

  const race = toTitleCase(state.EventData?.race?.meetingOfficialName || '');

  const session = findTimetabledSession(state.EventData?.seasonContext?.timetables);

  return {
    colSpec,
    name: `Formula ${ 3 - 2 }`,
    description: dasherizeParts(
      race,
      session?.description || 'No active session'
    ),
    trackDataSpec
  };
};

const FLAG_MAP = {
  1: FlagState.GREEN,
  2: FlagState.YELLOW,
  4: FlagState.SC,
  5: FlagState.RED,
  6: FlagState.VSC,
  7: FlagState.VSC, // ending
}

const parseFlagState = (session, track) => {
  if (session?.Status === 'Ends' || session?.Status === 'Finished') {
    return FlagState.CHEQUERED;
  }

  if (session?.Status === 'Inactive') {
    return FlagState.NONE;
  }

  return FLAG_MAP[track?.Status] || FlagState.NONE;
};

const getTimeRemaining = (clock) => {
  if (clock.Remaining) {
    const remTime = parseTime(clock.Remaining);

    if (clock.Extrapolating) {
      const timestamp = new Date(clock.Utc).getTime();
      const delta = Date.now() - timestamp;
      return remTime - (delta / 1000);
    }

    return remTime;
  }
  return undefined;
};

const getTrackData = (w) => {
  if (w) {
    return [
      [w.TrackTemp, '°C'],
      [w.AirTemp, '°C'],
      [w.WindSpeed, ' m/s'],
      [w.WindDirection, '°'],
      [w.Humidity, '%'],
      [w.Pressure, ' mbar'],
      w.Rainfall === "1" ? 'Wet' : 'Dry'
    ];
  }
  return Array(7).fill('-');
};

const mapCarState = (car) => {
  if (car.KnockedOut) {
    return 'FIN';
  }
  if (car.Retired) {
    return 'RET';
  }
  if (car.InPit) {
    return 'PIT';
  }
  if (car.Stopped) {
    return 'STOP';
  }
  if (car.PitOut) {
    return 'OUT';
  }

  return 'RUN';
};

const TYRE_MAP = {
  'HARD': ['H', 'tyre-hard'],
  'MEDIUM': ['M', 'tyre-med'],
  'SOFT': ['S', 'tyre-soft'],
  'INTERMEDIATE': ['I', 'tyre-inter'],
  'WET': ['W', 'tyre-wet'],
};

const renderGapOrLaps = (raw) => {
  if (raw && raw !== '' && raw[0] === '-') {
    return `${-1 * raw} lap${raw === -1 ? '' : 's'}`;
  }
  return raw;
};

const renderSector = (sector) => {

  if (!sector) {
    return ['', ''];
  }

  if (!sector.Value) {
    return [parseTime(sector.PreviousValue), 'old']
  }

  let flag = '';

  if (sector.OverallFastest) {
    flag = 'sb';
  }
  else if (sector.PersonalFastest) {
    flag = 'pb';
  }

  return [parseTime(sector.Value), flag];
}

const renderLaptime = (time, defaultFlag='') => {
  const timeVal = parseTime(time?.Value);

  let flag = defaultFlag;

  if (time.OverallFastest) {
    flag = 'sb';
  }
  else if (time.PersonalFastest) {
    flag = 'pb';
  }

  return [timeVal, flag];
}

const mapCars = (state) => {
  const timing = Object.values(state.TimingData?.Lines || {}).sort(
    (a, b) => a.Line - b.Line
  );
  const sessionPart = (state.TimingData?.SessionPart || 0) - 1;

  return timing.map(
    t => {

      const driver = state.DriverList?.[t.RacingNumber] || {};
      const stints = state.TimingAppData?.Lines?.[t.RacingNumber]?.Stints || [];
      const currentStint = stints.length > 0 ? stints[stints.length - 1] : null;

      let gap = '';
      let interval = '';

      if (t.GapToLeader) {
        gap = t.GapToLeader;
      }
      else if (t.TimeDiffToFastest) {
        gap = t.TimeDiffToFastest
      }
      else if (sessionPart >= 0 && t.Stats[sessionPart]?.TimeDiffToFastest) {
        gap = t.Stats[sessionPart]?.TimeDiffToFastest;
      }

      if (t.IntervalToPositionAhead) {
        interval = t.IntervalToPositionAhead.Value;
      }
      else if (t.TimeDiffToPositionAhead) {
        interval = t.TimeDiffToPositionAhead;
      }
      else if (sessionPart >= 0 && t.Stats[sessionPart]?.TimeDifftoPositionAhead) {
        interval = t.Stats[sessionPart].TimeDifftoPositionAhead;
      }

      let best = t.BestLapTime;

      if (sessionPart > 0 && !t.KnockedOut) {
        best = t.BestLapTimes[sessionPart];
      }

      return [
        t.RacingNumber,
        mapCarState(t),
        `${driver.LastName?.toUpperCase()}, ${driver.FirstName}`,
        driver.TeamName,
        t.NumberOfLaps,
        currentStint ? TYRE_MAP[currentStint.Compound] : ['', ''],
        currentStint ? currentStint.TotalLaps - currentStint.StartLaps || '0' : null,
        currentStint ? currentStint.TotalLaps || '0' : null,
        renderGapOrLaps(gap),
        renderGapOrLaps(interval),
        renderSector(t.Sectors[0]),
        renderSector(t.Sectors[1]),
        renderSector(t.Sectors[2]),
        t.Speeds?.ST?.Value,
        renderLaptime(t.LastLapTime),
        renderLaptime(best, 'old'),
        t.NumberOfPitStops
      ];

    }
  )

}

const blIdx = colSpec.indexOf(Stat.BEST_LAP);
const bs1Idx = colSpec.indexOf(Stat.BS1);
const stateIdx = colSpec.indexOf(Stat.STATE);

const postprocessCars = (cars) => {

  const bestLap = { };
  //const bestSectors = { 0: {}, 1: {}, 2: {} };

  cars.forEach(
    (car, idx) => {
      const bl = car[blIdx][0];
      if (bl && (!bestLap.time || bestLap.time > bl)) {
        bestLap.time = bl;
        bestLap.index = idx;
      }

      // [0, 1, 2].forEach(
      //   sector => {
      //     const time = car[bs1Idx + (2 * sector)][0];
      //     if (time && (!bestSectors[sector].time || bestSectors[sector].time > time)) {
      //       bestSectors[sector].time = time;
      //       bestSectors[sector].index = idx;
      //     }
      //   }
      // );
    }
  );

  if (bestLap.index >= 0) {
    cars[bestLap.index][blIdx] = [bestLap.time, 'sb'];
    if (cars[bestLap.index][blIdx - 1][0] === bestLap.time) {

      const hasLatestS3Time = cars[bestLap.index][bs1Idx + 3][0] !== '';
      const isRunning = cars[bestLap.index][stateIdx] === 'RUN';

      const canShowSBNew = hasLatestS3Time && isRunning;

      cars[bestLap.index][blIdx - 1][1] = canShowSBNew ? 'sb-new' : 'sb';
    }
  }

  // [0, 1, 2].forEach(
  //   sector => {
  //     const { index } = bestSectors[sector];
  //     if (index >= 0) {
  //       cars[index][bs1Idx + (2 * sector)][1] = 'sb';
  //     }
  //   }
  // );

  return cars;
};

export const translate = (state) => {

  const cars = postprocessCars(mapCars(state));

  const clock = state.ExtrapolatedClock || {};

  const session = {
    flagState: parseFlagState(state.SessionStatus, state.TrackStatus),
    timeRemain: getTimeRemaining(clock),
    pauseClocks: !clock.Extrapolating,
    trackData: getTrackData(state.WeatherData)
  };

  if (state.LapCount?.TotalLaps && state.EventData?.seasonContext?.state !== 'QUALIFYING') {
    session.lapsRemain = Math.floor(Math.max(0, state.LapCount.TotalLaps - state.LapCount.CurrentLap + 1));
  }

  return {
    cars,
    session
  };
};
